import { useMutation } from '@tanstack/react-query'
import { UserPlus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { FormError } from '@/components/FormError'
import { Loader } from '@/components/Loader'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { bothKnow, linkFace, useLinkView, type Asked } from '@/lib/connections'
import { Together } from './Together'

// Someone's connect link, scanned or opened: their face and name, who you both know, and a way to ask. Once you've
// asked, this waits on them, checking every few seconds, so their yes shows up here while you're standing together.
// Then you're both shown that you're connected.
export function LinkAsk({ token, onDone, page }: { token: string; onDone: () => void; page?: boolean }) {
  // On its own page the name is the page's heading; in a sheet it's under the sheet's.
  const Name = page ? 'h1' : 'h2'
  const view = useLinkView(token, { watch: true })
  const [note, setNote] = useState('')
  // Asked from this screen, so a connection that comes in is the one we were waiting for.
  const [asked, setAsked] = useState(false)
  const ask = useMutation({
    mutationFn: () =>
      api<Asked>(`/connect/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: note.trim() ? { note: note.trim() } : {},
      }),
    onSuccess: async () => {
      setAsked(true)
      await view.refetch()
    },
  })
  function submit(e: FormEvent) {
    e.preventDefault()
    ask.mutate()
  }
  if (view.isPending) return <Loader className="mx-auto my-10" />
  if (view.isError)
    return (
      <div className="flex flex-col gap-3">
        <FormError error={view.error} />
        <p className="text-muted-foreground">Ask them for a fresh link, or connect by email on People.</p>
      </div>
    )
  const them = view.data
  // Opened again while waiting: their yes still counts as the one we're waiting for.
  if (them.state === 'asked' && !asked) setAsked(true)
  const first = them.name.trim().split(/\s+/)[0] || them.name
  const face = { id: them.id, name: them.name, portrait: null, src: them.face ? linkFace(token) : undefined }
  if (them.state === 'connected' && (asked || ask.data?.connected)) return <Together other={face} onDone={onDone} />
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      {face.src ? (
        <span className="size-28 overflow-hidden rounded-full bg-secondary shadow-paper">
          <img src={face.src} alt="" className="size-full object-cover" />
        </span>
      ) : (
        <Avatar person={face} variant="display" className="size-28 text-5xl" />
      )}
      <div className="flex flex-col gap-1">
        <Name className="text-title text-balance">{them.state === 'you' ? 'This is your link' : them.name}</Name>
        {them.state !== 'you' && (
          <p className="text-sub text-balance text-muted-foreground">
            {them.mutual.length
              ? `You both know ${bothKnow(them.mutual)}.`
              : `${first} shared their connect link with you.`}
          </p>
        )}
      </div>
      {them.state === 'you' ? (
        <p className="text-balance">
          Anyone who opens it can ask to connect with you, and you choose whether to accept.
        </p>
      ) : them.state === 'connected' ? (
        <>
          <p role="status">You and {first} are already connected.</p>
          <Button className="w-full" onClick={onDone}>
            Done
          </Button>
        </>
      ) : them.state === 'asked' ? (
        <div className="flex w-full flex-col items-center gap-4">
          <p role="status" className="text-balance">
            Asked. Waiting for {first} to say yes…
          </p>
          <span aria-hidden>
            <Loader now label="Waiting" />
          </span>
          <p className="text-sub text-balance text-muted-foreground">
            If their link is still open, they’ll see you there. Otherwise it waits on their People page.
          </p>
          <Button variant="ghost" onClick={onDone}>
            Close
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex w-full flex-col gap-4 text-left">
          <p className="text-center text-sub text-balance text-muted-foreground">
            Once {first} accepts, you each see what the other shares with Everyone. Either of you can end it any time.
          </p>
          <div className="grid gap-2">
            <Label htmlFor="link-note">A note (optional)</Label>
            <Textarea
              id="link-note"
              maxLength={280}
              rows={2}
              placeholder="Say who you are"
              value={note}
              onChange={(e) => setNote(e.target.value.replace(/[\r\n]+/g, ' '))}
            />
          </div>
          <FormError error={ask.error} />
          <Button type="submit" disabled={ask.isPending}>
            <UserPlus aria-hidden /> Ask to connect
          </Button>
        </form>
      )}
    </div>
  )
}
